import React, { useCallback } from "react";
import { Routes, Route, useNavigate } from "react-router";
import LeftSideNav from "./sidenav/LeftSideNav";
import config from "config";
import OverviewContainer from "../containers/OverviewContainer/OverviewContainer";
import AlarmsViewContainer from "../containers/AlarmsViewContainer/AlarmsViewContainer";
import TrendContainer from "../containers/TrendContainer/TrendContainer";
import ReportsContainer from "../containers/ReportsContainer";
import SettingsContainer from "../containers/SettingsContainer/SettingsContainer";
import GraphicalUIContainer from "../containers/GraphicalUIContainer/GraphicalUIContainer";
import SampleViewContainer from "../containers/SampleViewContainer/SampleViewContainer";
import ListViewContainer from "../containers/ListViewContainer/ListViewContainer";
import EdgeUnitStatusPanel from "./edgeUnitStatusPanel/EdgeUnitStatusPanel";
import { useSettingsUI } from "../hooks/useReduxSelectors";
import { Box, Drawer } from "@mui/material";

const MENU_COMPONENTS = {
  overview: OverviewContainer,
  "list-view": ListViewContainer,
  "graphical-view": GraphicalUIContainer,
  "sample-view": SampleViewContainer,
  alarms: AlarmsViewContainer,
  trend: TrendContainer,
  report: ReportsContainer,
  settings: SettingsContainer,
};

const DashboardContent = ({ currentSelectedMenu, onSelect, systemName, isCompact, isMobile, containerRef, leftDrawerOpen, onCloseLeftDrawer, dashboardCount }) => {
  const navigate = useNavigate();
  const showEdgeUnitStatusPanel = useSettingsUI()?.showEdgeUnitStatusPanel;

  const handleSelect = useCallback(
    (selectedMenu) => {
      onSelect(selectedMenu);
      if (config.APP_ENV === "Edge") {
        const to = `/${selectedMenu}`;
        if (location.pathname !== to) {
          navigate(to);
        }
      }
    },
    [onSelect, navigate]
  );

  const handleSelectCompact = useCallback(
    (selectedMenu) => {
      handleSelect(selectedMenu);
      if (onCloseLeftDrawer) onCloseLeftDrawer();
    },
    [handleSelect, onCloseLeftDrawer]
  );

  const renderContent = () => {
    if (config.APP_ENV === "Edge") {
      return (
        <Routes>
          {Object.entries(MENU_COMPONENTS).map(([key, Comp]) => (
            <Route
              key={key}
              path={`/${key}`}
              element={key === "report" ? <Comp systemName={systemName} isCompact={isCompact} /> : <Comp isCompact={isCompact} />}
            />
          ))}
          <Route path="/" element={<OverviewContainer isCompact={isCompact} />} />
        </Routes>
      );
    }

    const key = currentSelectedMenu?.toLowerCase();
    const Comp = MENU_COMPONENTS[key] || OverviewContainer;
    return key === "report" ? <Comp systemName={systemName} isCompact={isCompact} /> : <Comp isCompact={isCompact} />;
  };

  const selectedMenu = currentSelectedMenu ? currentSelectedMenu.toLowerCase() : "overview";

  return (
    <>
      {isCompact ? (
        <Drawer
          open={leftDrawerOpen}
          onClose={onCloseLeftDrawer}
          ModalProps={{ disablePortal: true, keepMounted: true, disableAutoFocus: true, disableRestoreFocus: true }}
          sx={{
            position: "absolute",
            "& .MuiDrawer-paper": { position: "absolute", width: 240, bgcolor: "background.mid" },
            "& .MuiBackdrop-root": { position: "absolute" },
          }}
        >
          <LeftSideNav onSelect={handleSelectCompact} selectedMenu={selectedMenu} variant="expanded" />
        </Drawer>
      ) : (
        <LeftSideNav onSelect={handleSelect} selectedMenu={selectedMenu} />
      )}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minWidth: 0,
          // minHeight:0 lets this flex child shrink below its content size so the
          // inner view (e.g. OverviewContainer, which is height:100% overflow:auto)
          // scrolls internally instead of stretching the whole dashboard.
          minHeight: 0,
          m: { xs: 0.5, md: 1 },
          overflowX: "hidden",
          // backgroundColor: "#5e5e5e"
        }}
        id="DashboardContentContainer"
      >
        {showEdgeUnitStatusPanel && <EdgeUnitStatusPanel />}
        {renderContent()}
      </Box>
    </>
  );
};

export default DashboardContent;
